import { defineAsyncComponent } from 'vue';

import AccordionCustomizableFi from './accordion/AccordionCustomizableFi.vue';
import AccordionCustomizableWrapperFi from './accordion/AccordionCustomizableWrapperFi.vue';
import AccordionItemFi from './accordion-item-fi/AccordionItemFi.vue';
import ArrowIcon from './arrow-icon/ArrowIcon.vue';
import AutocompleteTextInputFi from './autocomplete-text-input-fi/AutocompleteTextInputFi.vue';
import AvailabilityIndicator from './availability-indicator/AvailabilityIndicator.vue';
import AvailabilityStatus from './availability-status/AvailabilityStatus.vue';
import BasePriceFi from './base-price-fi/BasePriceFi.vue';
import BreadcrumbFi from './breadcrumb-fi/BreadcrumbFi.vue';
import BubbledItem from './bubbled-item/BubbledItem.vue';
import ButtonFi from './button-fi/ButtonFi.vue';
import CarouselSlideFi from './carousel-fi/carousel-slide-fi/CarouselSlideFi.vue';
import CarouselFi from './carousel-fi/CarouselFi.vue';
import ChangeCustomerEmailFi from './change-customer-email-fi/ChangeCustomerEmailFi.vue';
import CloudImageFi from './cloud-image-fi/CloudImageFi.vue';
import CountryFlagFi from './country-flag-fi/CountryFlagFi.vue';
import DialogModalFi from './dialog-modal-fi/DialogModalFi.vue';
import ErrorMessage from './error-message/ErrorMessage.vue';
import FacetListFi from './facet-list-fi/FacetListFi.vue';
import HeadlineFi from './headline-fi/HeadlineFi.vue';
import IconAttributeItemFi from './icon-attribute-item-fi/IconAttributeItemFi.vue';
import IconFi from './icon-fi/IconFi.vue';
import LabelFi from './label-fi/LabelFi.vue';
import LdJson from './ld-json/LdJson.vue';
import LinkFi from './link-fi/LinkFi.vue';
import LinkFieldFi from './link-field-fi/LinkFieldFi.vue';
import LoadingIndicator from './loading-indicator/LoadingIndicator.vue';
import LoadingIndicatorWrapper from './loading-indicator/LoadingIndicatorWrapper.vue';
import LogoHeaderFi from './logo-header-fi/LogoHeaderFi.vue';
import MultiDataSourceProviderFi from './multi-data-source-provider-fi/MultiDataSourceProviderFi.vue';
import MultiLineTextFieldFi from './multi-line-text-field-fi/MultiLineTextFieldFi.vue';
import NoResultsFi from './no-results-fi/NoResultsFi.vue';
import PaginationProgressBar from './pagination-progress-bar/PaginationProgressBar.vue';
import PlaceholderFi from './placeholder-fi/PlaceholderFi.vue';
import PriceSavingsFi from './price-savings-fi/PriceSavingsFi.vue';
import PriceSavingsInPercentFi from './price-savings-in-percent-fi/PriceSavingsInPercentFi.vue';
import ProductPackageSizeFi from './product-package-size-fi/ProductPackageSizeFi.vue';
import ProductPartialsFi from './product-partials-fi/ProductPartialsFi.vue';
import QuantityModificationHintFi from './quantity-modification-hint-fi/QuantityModificationHintFi.vue';
import RichtextFi from './richtext-fi/RichtextFi.vue';
import SelectFi from './select-fi/SelectFi.vue';
import SelectInputFi from './select-input-fi/SelectInputFi.vue';
import SelectOptionsWrapperFi from './select-options-wrapper-fi/SelectOptionsWrapperFi.vue';
import SelectTextInputFi from './select-text-input-fi/SelectTextInputFi.vue';
import SingleLineTextFieldFi from './single-line-text-field-fi/SingleLineTextFieldFi.vue';
import SlideUpDownFi from './slide-up-down-fi/SlideUpDownFi.vue';
import SliderFi from './slider-fi/SliderFi.vue';
import SsrCloudImageFi from './ssr-cloud-image-fi/SsrCloudImageFi.vue';
import TextInputFi from './text-input-fi/TextInputFi.vue';
import TextareaFi from './textarea-fi/TextareaFi.vue';
import TooltipFi from './tooltip-fi/TooltipFi.vue';
import UniversalBox from './universal-box/UniversalBox.vue';

const ButtonFiIcon = defineAsyncComponent(() => import('./button-fi-icon/ButtonFiIcon.vue'));
const ChangePasswordFi = defineAsyncComponent(
  () => import('./change-password-fi/ChangePasswordFi.vue'),
);
const CheckboxFi = defineAsyncComponent(() => import('./checkbox-fi/CheckboxFi.vue'));
const CheckboxGroupFi = defineAsyncComponent(
  () => import('./checkbox-group-fi/CheckboxGroupFi.vue'),
);
const CountBadgeFi = defineAsyncComponent(() => import('./count-badge-fi/CountBadgeFi.vue'));
const HybrisAddressFi = defineAsyncComponent(
  () => import('./hybris-address-fi/HybrisAddressFi.vue'),
);
const RadioFi = defineAsyncComponent(() => import('./radio-fi/RadioFi.vue'));
const RadioGroupFi = defineAsyncComponent(() => import('./radio-group-fi/RadioGroupFi.vue'));
const RadioGroupWrapperFi = defineAsyncComponent(
  () => import('./radio-group-wrapper-fi/RadioGroupWrapperFi.vue'),
);
const RadioCardFi = defineAsyncComponent(() => import('./radio-card-fi/RadioCardFi.vue'));
const RangeSliderFi = defineAsyncComponent(() => import('./range-slider-fi/RangeSliderFi.vue'));
const CheckboxCustomFi = defineAsyncComponent(
  () => import('./checkbox-custom-fi/CheckboxCustomFi.vue'),
);
const CheckboxWrapperFi = defineAsyncComponent(
  () => import('./checkbox-wrapper-fi/CheckboxWrapperFi.vue'),
);
const PasswordRequirements = defineAsyncComponent(
  () => import('./password-requirements/PasswordRequirements.vue'),
);
const SitecoreIconFi = defineAsyncComponent(() => import('./sitecore-icon-fi/SitecoreIconFi.vue'));
const FileUploadFi = defineAsyncComponent(() => import('./file-upload-fi/FileUploadFi.vue'));
const DatepickerFi = defineAsyncComponent(() => import('./datepicker-fi/DatepickerFi.vue'));
const CustomDatepickerFi = defineAsyncComponent(
  () => import('./custom-datepicker-fi/CustomDatepickerFi.vue'),
);
const ToggleFi = defineAsyncComponent(() => import('./toggle-fi/ToggleFi.vue'));
const TooltipInteractiveFi = defineAsyncComponent(
  () => import('./tooltip-interactive-fi/TooltipInteractiveFi.vue'),
);
const HiddenInputFi = defineAsyncComponent(() => import('./hidden-input-fi/HiddenInputFi.vue'));
const CheckboxGroupWrapperFi = defineAsyncComponent(
  () => import('./checkbox-group-wrapper-fi/CheckboxGroupWrapperFi.vue'),
);
const ContextMenuFi = defineAsyncComponent(() => import('./context-menu-fi/ContextMenuFi.vue'));
const SimpleSelectFi = defineAsyncComponent(() => import('./simple-select-fi/SimpleSelectFi.vue'));
const AccessPrivilegeListFi = defineAsyncComponent(
  () => import('./access-privilege-list-fi/AccessPrivilegeListFi.vue'),
);
const AccessPrivilegesSelectionFi = defineAsyncComponent(
  () => import('./access-privileges-selection-fi/AccessPrivilegesSelectionFi.vue'),
);
const ExperienceEditorComponent = defineAsyncComponent(
  () => import('./experience-editor-component/ExperienceEditorComponent.vue'),
);
const FormatDictFi = defineAsyncComponent(() => import('./format-dict-fi/FormatDictFi.vue'));

const SimpleMultiselectOptionsFi = defineAsyncComponent(
  () => import('./simple-multiselect-options-fi/SimpleMultiselectOptionsFi.vue'),
);

const LayoutBlockFi = defineAsyncComponent(() => import('./layout-block-fi/LayoutBlockFi.vue'));

const TimepickerFi = defineAsyncComponent(() => import('./timepicker-fi/TimepickerFi.vue'));

const ProgressBarFi = defineAsyncComponent(() => import('./progress-bar-fi/ProgressBarFi.vue'));

const CarouselSliderControlsFi = defineAsyncComponent(
  () => import('./carousel-fi/CarouselSliderControlsFi.vue'),
);

const ProductListMostRecentlyEmptyStateFi = defineAsyncComponent(
  () => import('./product-list-most-recently-empty/ProductListMostRecentlyEmptyStateFi.vue'),
);

const ProductListMostRecentlyFi = defineAsyncComponent(
  () => import('./product-list-most-recently/ProductListMostRecentlyFi.vue'),
);

const SpecialLoaderFi = defineAsyncComponent(
  () => import('./special-loader-fi/SpecialLoaderFi.vue'),
);

export {
  IconAttributeItemFi,
  AccordionItemFi,
  AccordionCustomizableFi,
  AccordionCustomizableWrapperFi,
  ArrowIcon,
  AutocompleteTextInputFi,
  BasePriceFi,
  BubbledItem,
  ButtonFi,
  ButtonFiIcon,
  ChangeCustomerEmailFi,
  ChangePasswordFi,
  CheckboxFi,
  CheckboxGroupFi,
  CloudImageFi,
  CountBadgeFi,
  ErrorMessage,
  LinkFi,
  HeadlineFi,
  HybrisAddressFi,
  RichtextFi,
  IconFi,
  LayoutBlockFi,
  TimepickerFi,
  ToggleFi,
  TooltipFi,
  TooltipInteractiveFi,
  LoadingIndicator,
  LoadingIndicatorWrapper,
  LogoHeaderFi,
  SelectFi,
  SelectInputFi,
  SelectTextInputFi,
  SelectOptionsWrapperFi,
  SimpleMultiselectOptionsFi,
  SitecoreIconFi,
  SsrCloudImageFi,
  FacetListFi,
  TextInputFi,
  TextareaFi,
  LabelFi,
  LdJson,
  LinkFieldFi,
  UniversalBox,
  RangeSliderFi,
  MultiDataSourceProviderFi,
  MultiLineTextFieldFi,
  SingleLineTextFieldFi,
  SliderFi,
  SlideUpDownFi,
  CarouselFi,
  CarouselSlideFi,
  CarouselSliderControlsFi,
  CountryFlagFi,
  DatepickerFi,
  CustomDatepickerFi,
  BreadcrumbFi,
  RadioFi,
  RadioCardFi,
  RadioGroupFi,
  RadioGroupWrapperFi,
  CheckboxWrapperFi,
  CheckboxCustomFi,
  FileUploadFi,
  ProductListMostRecentlyEmptyStateFi,
  ProductListMostRecentlyFi,
  ProductPackageSizeFi,
  ProductPartialsFi,
  QuantityModificationHintFi,
  DialogModalFi,
  HiddenInputFi,
  CheckboxGroupWrapperFi,
  ContextMenuFi,
  SimpleSelectFi,
  AccessPrivilegeListFi,
  AccessPrivilegesSelectionFi,
  AvailabilityIndicator,
  AvailabilityStatus,
  PaginationProgressBar,
  PlaceholderFi,
  PriceSavingsFi,
  PriceSavingsInPercentFi,
  ProgressBarFi,
  FormatDictFi,
  PasswordRequirements,
  NoResultsFi,
  ExperienceEditorComponent,
  SpecialLoaderFi,
};
